export type FeaturedListingItem = {
  listingId: number;
  listingNumber: string | null;
  statusName: string | null;
  listingStatusTag: string | null;
  addressLine: string | null;
  suburb: string | null;
  city: string | null;
  price: number | null;
  mainImageUrl?: string | null;
  selected?: boolean;
  eligible?: boolean;
};

export const MAX_FEATURED_LISTINGS = 24;
export type FeaturedListingsResponse = { items: FeaturedListingItem[] };
export const featuredListingsQueryKey = ['home-featured-listings'] as const;
export const featuredListingsSearchKey = (query: string) => ['home-featured-listings-search', query] as const;
export const localFeaturedDraftEnabled = () => import.meta.env.DEV && import.meta.env.VITE_LOCAL_READ_ONLY_MODE === 'true';
const localDraftOrigin = 'http://127.0.0.1:4298';
export type LocalFeaturedDraft = { mode: 'local_draft'; exists: boolean; kwuid: string; savedAt: string | null; listingIds: number[]; items: FeaturedListingItem[]; excludedIds: number[] };
type LocalListing = { id: number; listingNumber: string | null; mainImageUrl: string | null; suburb: string | null; city: string | null; price: number | null; statusTag: string | null };
function localItem(item: LocalListing): FeaturedListingItem {
  return { listingId: item.id, listingNumber: item.listingNumber, mainImageUrl: item.mainImageUrl, suburb: item.suburb, city: item.city, price: item.price, statusName: 'Active', listingStatusTag: item.statusTag, addressLine: null };
}
async function localRequest(path: string, token: string, context: string, body?: unknown) {
  if (!localFeaturedDraftEnabled()) throw new Error('Local drafts are unavailable outside the read-only development environment.');
  const response = await fetch(`${localDraftOrigin}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${token}`, 'X-Active-Context': context, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error ?? 'Local draft service is unavailable.');
  return payload;
}
export async function fetchLocalFeaturedDraft(token: string, context: string): Promise<LocalFeaturedDraft> {
  const result = await localRequest('/draft', token, context);
  return { ...result, items: result.items.map(localItem) };
}
export async function searchLocalFeaturedListings(query: string, token: string, context: string): Promise<FeaturedListingsResponse> {
  const result = await localRequest(`/search?q=${encodeURIComponent(query)}`, token, context);
  return { items: result.items.map(localItem) };
}
export async function saveLocalFeaturedDraft(listingIds: number[], token: string, context: string): Promise<LocalFeaturedDraft> {
  const result = await localRequest('/draft', token, context, { listingIds });
  return { ...result, items: result.items.map(localItem) };
}
export async function previewLocalFeaturedDraft(token: string, context: string): Promise<{ url: string; expiresAt: string }> {
  const result = await localRequest('/preview', token, context, {});
  const url = new URL(result.url);
  if (url.origin !== 'http://127.0.0.1:4294' || !/^\/[0-9]{3,12}$/.test(url.pathname) || !/^#local-featured-preview=[A-Za-z0-9_-]{43}$/.test(url.hash)) throw new Error('Local preview destination could not be verified.');
  return { url: url.href, expiresAt: result.expiresAt };
}

export function toggleFeaturedListingIds(currentIds: number[], listingId: number): number[] {
  if (currentIds.includes(listingId)) return currentIds.filter((id) => id !== listingId);
  return currentIds.length >= MAX_FEATURED_LISTINGS ? currentIds : [...currentIds, listingId];
}

export function moveFeaturedListingId(currentIds: number[], index: number, direction: -1 | 1): number[] {
  const target = index + direction;
  if (index < 0 || target < 0 || index >= currentIds.length || target >= currentIds.length) return currentIds;
  const next = [...currentIds];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function orderFeaturedListingItems(ids: number[], searched: FeaturedListingItem[], selected: FeaturedListingItem[]): FeaturedListingItem[] {
  const searchedById = new Map(searched.map((item) => [item.listingId, item] as const));
  return ids.map((id) => searchedById.get(id) ?? selected.find((item) => item.listingId === id)).filter((item): item is FeaturedListingItem => Boolean(item));
}

export function hasFeaturedListingChanges(draftIds: number[] | null, persistedIds: number[]): boolean {
  return draftIds !== null && (draftIds.length !== persistedIds.length || draftIds.some((id, index) => id !== persistedIds[index]));
}

export async function fetchFeaturedListings(token: string): Promise<FeaturedListingsResponse> {
  const response = await fetch('/api/agents/me/featured-listings', { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error('Unable to load featured listings');
  return response.json() as Promise<FeaturedListingsResponse>;
}

export async function searchFeaturedListings(query: string, token: string): Promise<FeaturedListingsResponse> {
  const params = new URLSearchParams();
  if (query.trim()) params.set('q', query.trim());
  const response = await fetch(`/api/agents/me/featured-listings/search?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error('Unable to search listings');
  return response.json() as Promise<FeaturedListingsResponse>;
}

export async function saveFeaturedListings(listingIds: number[], token: string): Promise<FeaturedListingsResponse> {
  if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_READ_ONLY_MODE === 'true') {
    throw new Error('Saving Featured Listings is disabled in this read-only session.');
  }
  const response = await fetch('/api/agents/me/featured-listings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ listingIds }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(typeof body?.error === 'string' ? body.error : 'Unable to save featured listings');
  }
  return response.json() as Promise<FeaturedListingsResponse>;
}

export function resolveFeaturedImageUrl(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  const apiBaseUrl = ((import.meta.env.VITE_API_URL as string | undefined) ?? '').replace(/\/$/, '');
  if (raw.startsWith('/uploads/')) return apiBaseUrl ? `${apiBaseUrl}${raw}` : raw;
  const uploadHostMatch = raw.match(/^https?:\/\/[^/]+(\/uploads\/.+)$/i);
  if (uploadHostMatch) return apiBaseUrl ? `${apiBaseUrl}${uploadHostMatch[1]}` : uploadHostMatch[1];
  return raw;
}
